import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useFeatureFlagFromContext } from '../../launchdarkly';
import apiEndpoint from '../config/data';
import { getAuthHeader } from '../../utils/tokenService';
import { getBlid } from '../../utils/cookieHelpers';

/**
 * Route for the Manage Subscription / "Subscription & Billing" page.
 * The page itself is delivered separately (PROF-1191); both the trial banner
 * and the account-menu item navigate here.
 */
export const MANAGE_SUBSCRIPTION_PATH = '/subscription-billing';

/** LaunchDarkly flag gating the trial-expiring experience (banner + menu item). */
export const TRIAL_BANNER_FLAG = 'trialExpiration';

/**
 * Base host for the Profit Assist BFF. When unset, the trials path is left
 * relative and resolved against `apiEndpoint` (`REACT_APP_API_ENDPOINT`) below —
 * the Django API host in dev/QA, and genuinely same-origin only in prod builds
 * where `REACT_APP_API_ENDPOINT` is empty.
 */
const TRIALS_API_BASE = process.env.REACT_APP_PROFIT_ASSIST_BFF_URL || '';

/**
 * Builds the Profit Assist BFF trials-read URL (`GET /api/v1/trials/:blid`) for
 * a merchant. Per the Trials API integration guide the FE calls the BFF
 * directly from the browser — there is no Django proxy. Auth is the Dashboard
 * Okta session cookie, forwarded automatically because the fetch below sets
 * `credentials: 'include'`. BLID comes from the `PA_BLID` cookie. No trailing
 * slash — the BFF route is `/api/v1/trials/:blid` exactly.
 */
const buildTrialStatusPath = blid =>
  `${TRIALS_API_BASE}/api/v1/trials/${encodeURIComponent(blid)}`;

/**
 * Fallback per-location monthly price shown until the subscription-status API
 * (PROF-1192) supplies a real value. This is the current self-serve list price.
 */
export const DEFAULT_PRICE_PER_LOCATION = 100;

// Inert (hidden) status returned until real trial data is available.
const emptyStatus = (merchantKey = 'default') => ({
  isLoading: false,
  isSelfServe: false,
  isTrialing: false,
  isCancelled: false,
  trialEndDate: null,
  chargeDate: null,
  pricePerLocation: DEFAULT_PRICE_PER_LOCATION,
  merchantKey,
});

/**
 * Maps the Profit Assist BFF trial-status record to the shape consumed by the
 * banner, account-menu item, and Subscription & Billing page.
 *
 * Prefers the FE-friendly derived shape the BFF now returns
 * (`{ source, isTrialing, isCancelled, effectiveEndsAt }`; `accessEndsAt` is
 * accepted as a synonym for the access-end date) and falls back to the legacy
 * `TrialRecord` fields (`trialEndsAt`, no cancellation signal) so a pre-migration
 * response still renders. `isTrialing` is server-authoritative when supplied and
 * only computed from the access-end date (vs. the client clock) as a fallback.
 *
 * Notes from the Trials API integration guide:
 * - Timestamps are ISO-8601 with an explicit offset (e.g. `+00:00`), not a
 *   trailing `Z`. `new Date(...)` / date-fns `parseISO` handle both; never
 *   regex for `Z`.
 * - `source === 'self_serve'` identifies the self-serve cohort this experience
 *   is scoped to.
 * - `TrialRecord` carries no `chargeDate`/price; those come from the
 *   subscription-status API (PROF-1192), so they are read defensively here.
 */
export const mapTrialRecordToStatus = (record, merchantKey = 'default') => {
  if (!record) return emptyStatus(merchantKey);

  // Single "access ends" date: the derived shape's effectiveEndsAt/accessEndsAt,
  // else the legacy trialEndsAt.
  const trialEndDate =
    record.effectiveEndsAt ?? record.accessEndsAt ?? record.trialEndsAt ?? null;
  // Trust the server-derived flag when present; otherwise fall back to the old
  // client-clock comparison against the access-end date.
  const isTrialing =
    typeof record.isTrialing === 'boolean'
      ? record.isTrialing
      : trialEndDate
        ? new Date(trialEndDate).getTime() > Date.now()
        : false;

  return {
    isLoading: false,
    isSelfServe: record.source === 'self_serve',
    isTrialing,
    isCancelled: record.isCancelled === true,
    trialEndDate,
    chargeDate: record.chargeDate || null,
    pricePerLocation: record.pricePerLocation ?? DEFAULT_PRICE_PER_LOCATION,
    merchantKey,
  };
};

// Display-only probe for the trial record. Intentionally does NOT use
// `fetchJsonWithAuth`/`fetchWithAuth`: those log the user out on a 401 (clearing
// tokens, deleting SSO cookies, and redirecting to re-auth) before throwing, so
// a background eligibility check the user never initiated could trigger a full
// auth reset. Here any non-OK response — 401 included — resolves to `null`
// (hidden), so this read can never mutate Dashboard auth state. It still sends
// the Okta session cookie (`credentials: 'include'`) and the same Authorization
// header as the shared helper so SSO users authenticate identically.
const probeTrialRecord = async blid => {
  const path = buildTrialStatusPath(blid);
  const url = path.startsWith('http') ? path : `${apiEndpoint}${path}`;
  const authHeader = getAuthHeader();
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (!response.ok) return null;
  return response.json();
};

// In-flight cache so the banner and account menu (both call this hook) share a
// single request per BLID. The entry is dropped once the request settles — on
// both success and failure — so this only de-dupes concurrent requests. A later
// mount (e.g. after an operator extends the trial) always refetches and sees the
// current trial end date rather than a stale, session-cached one.
let trialRequestCache = { blid: null, promise: null };

const fetchTrialRecord = blid => {
  if (trialRequestCache.blid !== blid || !trialRequestCache.promise) {
    const promise = probeTrialRecord(blid);
    trialRequestCache = { blid, promise };
    const evict = () => {
      if (trialRequestCache.promise === promise) {
        trialRequestCache = { blid: null, promise: null };
      }
    };
    promise.then(evict, evict);
  }
  return trialRequestCache.promise;
};

// Subscribers (mounted hook instances) notified when the status is refreshed.
// A cancellation mutates the merchant's record server-side, so the read must be
// re-run and re-published to every consumer (trial banner + Subscription &
// Billing page) rather than waiting for a full reload.
const refreshSubscribers = new Set();

/**
 * Invalidate any cached read and re-fetch the merchant's status, pushing the
 * fresh record to all mounted `useSubscriptionStatus` consumers. Call after a
 * successful cancellation so the disabled cancel link and suppressed banner
 * reflect the new cancelled state immediately.
 */
export const refreshSubscriptionStatus = () => {
  // Drop the in-flight/settled cache so the next read hits the BFF instead of
  // reusing a pre-cancellation promise.
  trialRequestCache = { blid: null, promise: null };
  refreshSubscribers.forEach(notify => notify());
};

/**
 * Self-serve subscription/trial status used to gate the trial-expiring banner
 * and the "Subscription & Billing" account-menu item.
 *
 * Reads the merchant's trial record directly from the Profit Assist BFF
 * (`GET /api/v1/trials/:blid`, via `buildTrialStatusPath`) using the Dashboard
 * Okta session cookie. The fetch is gated on the `trialExpiration` flag so
 * nothing is requested until the experience is enabled, and it fails closed —
 * any error (404 = no trial row, network failure) leaves the experience hidden.
 *
 * NOTE: This hook is read-only display data. The Trials extend endpoint
 * (`POST /api/v1/trials/:blid/extend`) is an operator-only action and is not
 * used by this experience.
 *
 * @returns {{
 *   isEnabled: boolean,
 *   isLoading: boolean,
 *   isSelfServe: boolean,
 *   isTrialing: boolean,
 *   isCancelled: boolean,
 *   trialEndDate: (string|Date|null),
 *   chargeDate: (string|Date|null),
 *   pricePerLocation: number,
 *   merchantKey: string,
 *   refresh: () => void,
 * }}
 */
export const useSubscriptionStatus = () => {
  const user = useSelector(state => state.user);
  const enabled = useFeatureFlagFromContext(TRIAL_BANNER_FLAG, false);

  // Per-location dismissal key. The trial record is keyed by BLID, so the key is
  // too — a user with access to multiple BLIDs gets a distinct dismissal per
  // location instead of one dismissal suppressing the banner everywhere. Falls
  // back to the user id, then 'default', when no BLID cookie is present.
  const blid = getBlid();
  const merchantKey = blid
    ? `blid:${blid}`
    : user?.userId
      ? `user:${user.userId}`
      : 'default';

  const [status, setStatus] = useState(() => emptyStatus(merchantKey));
  // Bumped when refreshSubscriptionStatus() fires; feeds the fetch effect below
  // so a post-cancellation refresh re-runs the read for this instance.
  const [refreshTick, setRefreshTick] = useState(0);

  // Register this instance so an external refresh re-triggers its fetch effect.
  useEffect(() => {
    const notify = () => setRefreshTick(tick => tick + 1);
    refreshSubscribers.add(notify);
    return () => {
      refreshSubscribers.delete(notify);
    };
  }, []);

  useEffect(() => {
    // Only fetch once the experience is enabled and a BLID is present.
    if (!enabled || !blid) {
      setStatus(emptyStatus(merchantKey));
      return undefined;
    }

    let cancelled = false;
    // Reset eligibility before the request settles so a merchant/BLID change
    // never leaves the previous merchant's banner/menu visible (both consumers
    // ignore isLoading). Fails closed until the new response arrives.
    setStatus({ ...emptyStatus(merchantKey), isLoading: true });

    fetchTrialRecord(blid)
      .then(record => {
        if (!cancelled) setStatus(mapTrialRecordToStatus(record, merchantKey));
      })
      .catch(() => {
        if (!cancelled) setStatus(emptyStatus(merchantKey));
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, blid, merchantKey, refreshTick]);

  // Re-fetch and re-publish this + every other consumer's status. Exposed so a
  // successful cancellation can refresh the banner/menu/page without a reload.
  const refresh = useCallback(() => refreshSubscriptionStatus(), []);

  // Surface the flag so consumers gate off a single source instead of reading
  // the LaunchDarkly flag themselves.
  return { ...status, isEnabled: enabled, refresh };
};

export default useSubscriptionStatus;
